import './styles/InsertPieceStyle.css';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  LockerColumn,
  BaseInput,
  NavigationButton,
  TopNav,
  TopNavToastMessage,
  ToastType,
} from '../common';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import { useHistory } from 'react-router-dom';
import {
  LockerColumnStatuses,
  LockerColumnTypes,
  LockerPosition,
  LockerPositionStatus,
  LockerPositionType,
} from '../../models/microservices';
import Spinner from 'react-bootstrap/Spinner';
import {
  EntryConfirmationRequestPOST,
  EntryConfirmationResponsePOST,
} from '../../models/cruds';
import { apiErrorHandler, apiPOST } from '../../restApi';
import { Lock } from '../../devices/locker/interface/Locker/Lock';

const API_VALIDATION_ERROR = 'Error en la consulta';
const LOCKER_MESSAGE_OPENING = '¡Abriendo locker!';
const INSERTING_PIECE_API = 'Confirmando el ingreso en CRA';
const LOCKER_MESSAGE_OPENED =
  'Puedes insertar el paquete, por favor cierra la puerta, ¡Muchas gracias!';
const LOCKER_MESSAGE_CLOSED =
  'Paquete insertado, por favor confirma para terminar, ¡Muchas gracias!';
const PRIMARY_COLUMN_COMPARTMENT_LENGTH = 8;
const PRIMARY_MASTER_COMPARTMENT_INDEX = 1;
const PRIMARY_MASTER_COMPARTMENT = {
  lockerPositionType: {
    name: LockerColumnTypes.SCREEN,
  } as LockerPositionType,
  lockerPositionStatus: {
    name: LockerColumnStatuses.BLOCKED,
  } as LockerPositionStatus,
} as LockerPosition;

const InsertPiece: React.FC = () => {
  let history = useHistory();
  const insertLockRef = useRef<Lock>();
  const [loading, setLoading] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [insertLock, setInsertLock] = useState<Lock>();
  const [lockOpened, setLockOpened] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const agentToken = useTypedSelector(({ session }) => {
    return session.dhlAgentToken;
  });
  const currentLockerPosition = useTypedSelector(({ locker }) => {
    return locker.currentLockerPosition;
  });
  const currentPackageCandidate = useTypedSelector(({ locker }) => {
    return locker.currentPackageCandidate;
  });
  const lockerContent = useTypedSelector(({ locker }) => {
    return locker.lockerContent;
  });
  const locker = useTypedSelector(({ devices }) => {
    return devices.locker;
  });

  insertLockRef.current = insertLock;
  //On Loading
  useEffect(() => {
    console.log('on loading');
    addLockerEventListeners();
  }, []);
  //On unloading
  useEffect(
    () => () => {
      console.log('on unloading');
      removeLockerEventListeners();
    },
    []
  );
  const addLockerEventListeners = () => {
    locker?.addEventListener('lockOpened', onLockOpened as EventListener);
    locker?.addEventListener('lockClosed', onLockClosed as EventListener);
  };
  const removeLockerEventListeners = () => {
    locker?.removeEventListener('lockOpened', onLockOpened as EventListener);
    locker?.removeEventListener('lockClosed', onLockClosed as EventListener);
  };
  const onLockOpened = useCallback(
    (event: CustomEvent) => {
      const openedLock = event.detail as Lock;
      console.log('lock Opened', event.detail, insertLock, openedLock);
      if (
        openedLock.sid === insertLockRef.current?.sid &&
        openedLock.cid === insertLockRef.current?.cid
      ) {
        console.log('lock Opened true', event.detail);
        setInstructions(LOCKER_MESSAGE_OPENED);
        setLockOpened(true);
      }
    },
    [insertLock]
  );
  const onLockClosed = useCallback(
    (event: CustomEvent) => {
      const closedLock = event.detail as Lock;
      console.log('lock Closed ', insertLock, closedLock);
      if (
        closedLock.sid === insertLockRef.current?.sid &&
        closedLock.cid === insertLockRef.current?.cid
      ) {
        console.log('lock Closed true', event.detail);
        setInstructions(LOCKER_MESSAGE_CLOSED);
        setCanContinue(true);
        setLockOpened(false);
      }
    },
    [insertLock]
  );
  //#endregion
  const { updateCurrentLockerPosition } = useActions();
  useEffect(() => {
    if (lockOpened && !currentLockerPosition.retrieved) {
      console.log('Closing lock');
    }
  }, [lockOpened, currentLockerPosition, updateCurrentLockerPosition]);
  useEffect(() => {
    if (currentLockerPosition.retrieved) {
      setInstructions(LOCKER_MESSAGE_CLOSED);
      setCanContinue(true);
      setFeedbackMessage('');
    } else {
      setInstructions(LOCKER_MESSAGE_OPENING);
      setInsertLock(
        new Lock(
          currentLockerPosition.extraFields.sid,
          currentLockerPosition.extraFields.cid
        )
      );
    }
  }, [currentLockerPosition]);
  useEffect(() => {
    if (locker && insertLock?.sid && insertLock?.cid) {
      locker.openLockAsync(insertLock);
    }
  }, [locker, insertLock]);
  useEffect(() => {
    if (lockOpened && !currentLockerPosition.retrieved) {
      console.log('Closing lock');
    }
  }, [lockOpened, currentLockerPosition, updateCurrentLockerPosition]);
  const insertPieceAsync = async (): Promise<boolean> => {
    setLoading(true);
    setInstructions(INSERTING_PIECE_API);
    let packageCandidateId = currentPackageCandidate.id ?? 0;
    let lockerPositionId = currentLockerPosition.id;
    let request = new EntryConfirmationRequestPOST();
    let response: EntryConfirmationResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(packageCandidateId, lockerPositionId),
      request.generateConfig(agentToken)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        if (response.data.entryConfirmation) {
          history.replace(`/${navigationPages.INSERT_PROCESS_SELECTION}`);
          return true;
        }
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedbackMessage(API_VALIDATION_ERROR);
    setLoading(false);
    return false;
  };
  const renderLockerColumn = () => {
    if (loading) {
      return (
        <div className="insert-piece-locker-container">
          <div className="spinner-container">
            <Spinner className="spinner" animation="grow" variant="danger" />
          </div>
        </div>
      );
    }
    if (currentLockerPosition?.extraFields) {
      let currentColumn = lockerContent
        .filter(
          (positions) =>
            positions.extraFields.sid === currentLockerPosition.extraFields.sid
        )
        .sort((a, b) => a.row.localeCompare(b.row));
      console.log(currentColumn);
      console.log(currentLockerPosition);

      if (currentColumn.length === PRIMARY_COLUMN_COMPARTMENT_LENGTH) {
        currentColumn.splice(
          PRIMARY_MASTER_COMPARTMENT_INDEX,
          0,
          PRIMARY_MASTER_COMPARTMENT
        );
      }
      return (
        <div className="insert-piece-locker-container">
          <LockerColumn
            onSelectCompartment={(position) => {}}
            lockerColumn={currentColumn}
            currentCompartment={currentLockerPosition}
          />
        </div>
      );
    }
  };
  const renderContent = () => {
    return (
      <div className="locker-content-container">
        <div className="locker-content-container-left">
          <div className="insert-piece-input-container">
            <div className="insert-piece-awb-container">
              <BaseInput
                val={currentPackageCandidate.pieceNumber}
                inputLabel="Número de pieza"
                inputPlaceholder=""
                inError={false}
                maxLength={25}
                onTextChange={() => {}}
                extraClassContainer="no-padding-right full-width stretched"
                extraClass="placeholder-left"
              />
            </div>
            <div className="insert-piece-awb-container">
              <BaseInput
                val={currentPackageCandidate.awbNumber}
                inputLabel="Número de guía"
                inputPlaceholder=""
                inError={false}
                maxLength={25}
                onTextChange={() => {}}
                extraClassContainer="no-padding-right full-width stretched"
                extraClass="placeholder-left"
              />
            </div>
            <div className="insert-piece-awb-container">
              <p className="remove-piece-awb-container">{instructions}</p>
            </div>
          </div>
        </div>
        <div className="locker-content-container-right">
          {renderLockerColumn()}
        </div>
      </div>
    );
  };
  const renderNextPieceButton = () => {
    if (canContinue) {
      return (
        <NavigationButton
          buttonContent={languageMap.insert_piece.confirm_button[language]}
          buttonClass="navigation-button confirm-button"
          onClickButton={() => {
            insertPieceAsync();
          }}
        />
      );
    }
  };
  return (
    <div id="insertPiece" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container fullpage">
          <TopNavToastMessage
            toastText={feedbackMessage}
            toastType={ToastType.ERROR}
            resetMessage={() => setFeedbackMessage('')}
          />
          <div className="title-content-container">
            <p className="header nopadding nomargin">
              {languageMap.insert_piece.title[language]}
            </p>
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={languageMap.insert_piece.cancel_button[language]}
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(`/${navigationPages.INSERT_SCAN_PID}`);
                }}
              />
            </div>
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={languageMap.insert_piece.report_button[language]}
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(`/${navigationPages.INSERT_ISSUES}`);
                }}
              />
            </div>
            <div className="footer-button-container">
              {renderNextPieceButton()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export { InsertPiece };
